/*
 * © 2026 eggs unimedia GmbH
 * Coded with love 💛
 */

import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { Icon } from '../../index'
import logoImg from 'url:../../../../img/Logo_eggs_connect.webp'
import { useUser, logoutUser } from '../../../hooks/useUser'
import { useRole } from '../../../hooks/useRole'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'
import { useQueryClient } from '@tanstack/react-query'
import { fetchSchedule } from '../../../services/graphqlService'
import { createSlug } from '../../../utils/slugify'
import { sortByDate } from '../../../utils/dateUtils'

/* =====================================================
   Navigation Styles (lokal, zentral definiert)
===================================================== */

const navItemBase =
  'group relative block px-6 py-4 rounded-xl text-xl font-medium transition-all duration-300 min-h-[48px] flex items-center'

const navItemInactive = 'text-slate-700'

const navItemActive = 'bg-eggs-yellow text-eggs-dark shadow-md'

/* =====================================================
   NavItem Component
===================================================== */

function NavItem({ to, label, onClick, delay = 0 }) {
  return (
    <li style={{ animation: `slideInItem 0.7s ease-out ${delay}s both` }}>
      <NavLink
        to={to}
        onClick={onClick}
        className={({ isActive }) =>
          `${navItemBase} ${isActive ? navItemActive : navItemInactive}`
        }
      >
        {({ isActive }) => (
          <>
            <span className="relative z-10">{label}</span>

            {/* Active indicator */}
            <span
              className={`
                absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-full
                bg-eggs-yellow transition-opacity duration-300
                ${isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}
              `}
            />
          </>
        )}
      </NavLink>
    </li>
  )
}

/* =====================================================
   BurgerMenu
===================================================== */

function BurgerMenu({ isOpen, onClose, auth, signOutRedirect }) {
  const [schedules, setSchedules] = useState([])
  const { isLoggedIn, name } = useUser()
  const { role } = useRole()
  const { galleryEnabled } = useFeatureFlags()
  const queryClient = useQueryClient()
  const showRole = role && role !== 'attendee'
  const [probeIndustry, setProbeIndustry] = useState(
    () => localStorage.getItem('probe-industry-override') || '',
  )

  const handleIndustryChange = (value) => {
    setProbeIndustry(value)
    if (value) {
      localStorage.setItem('probe-industry-override', value)
    } else {
      localStorage.removeItem('probe-industry-override')
    }
    queryClient.invalidateQueries({ queryKey: ['probe-questions'] })
  }

  useEffect(() => {
    fetchSchedule()
      .then((data) => {
        const items = data?.data?.connectScheduleList?.items || []
        const sorted = sortByDate(items, 'date')
        setSchedules(sorted)
      })
      .catch((err) => {
        console.error('Failed to fetch schedules:', err)
      })
  }, [])

  if (!isOpen) return null

  const handleAuthAction = () => {
    if (isLoggedIn) {
      logoutUser()
    } else {
      window.location.href = '/login'
    }
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      style={{
        background:
          'linear-gradient(135deg, rgba(0,0,0,0.3), rgba(50,50,50,0.8))',
        backdropFilter: 'blur(12px)',
        animation: 'fadeIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      }}
      onClick={onClose}
    >
      <div
        className="h-full shadow-2xl relative overflow-y-auto flex flex-col"
        style={{
          width: '90vw',
          maxWidth: '400px',
          background:
            'linear-gradient(180deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%)',
          borderLeft: '1px solid rgba(148, 163, 184, 0.2)',
          animation: 'slideInRight 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
          boxShadow: '-10px 0 50px rgba(0, 0, 0, 0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-8 flex-1">
          {/* Header */}
          <div className="flex justify-between items-center mb-10">
            <div>
              <NavLink to="/" onClick={onClose}>
                <img src={logoImg} alt="eggs connect" className="w-auto max-w-[200px]" />
              </NavLink>
              <div className="w-22 mt-5 h-0.5 bg-gradient-to-r from-eggs-yellow to-white rounded-full" />
            </div>

            <button
              onClick={onClose}
              className="transition-all duration-300 opacity-70 hover:opacity-100 p-2"
            >
              <Icon name="x" size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav>
            <ul className="space-y-2">
              {schedules.map((schedule, idx) => (
                <NavItem
                  key={schedule._path}
                  to={`/${createSlug(schedule.title)}`}
                  label={schedule.title}
                  onClick={onClose}
                  delay={0.1 + idx * 0.1}
                />
              ))}
              <NavItem
                to="/speaker"
                label="Speaker"
                onClick={onClose}
                delay={0.1 + schedules.length * 0.1}
              />
              <NavItem
                to="/gondolas"
                label="Umadum Sessions"
                onClick={onClose}
                delay={0.2 + schedules.length * 0.1}
              />
              <li style={{ animation: `slideInItem 0.7s ease-out ${0.22 + schedules.length * 0.1}s both` }}>
                <a
                  href="https://www.eggsunimedia.com/de/de/services/offers"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                  className={`${navItemBase} ${navItemInactive}`}
                >
                  <span className="relative z-10 flex items-center gap-2">
                    Unsere Angebote
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 20 20" fill="none" className="shrink-0">
                      <path d="M0 17V6C0 5.20435 0.316297 4.44152 0.878906 3.87891C1.44152 3.3163 2.20435 3 3 3H9C9.55229 3 10 3.44772 10 4C10 4.55228 9.55229 5 9 5H3C2.73478 5 2.4805 5.10543 2.29297 5.29297C2.10543 5.4805 2 5.73478 2 6V17C2 17.2652 2.10543 17.5195 2.29297 17.707C2.48051 17.8946 2.73478 18 3 18H14C14.2652 18 14.5195 17.8946 14.707 17.707C14.8946 17.5195 15 17.2652 15 17V11C15 10.4477 15.4477 10 16 10C16.5523 10 17 10.4477 17 11V17C17 17.7957 16.6837 18.5585 16.1211 19.1211C15.5585 19.6837 14.7957 20 14 20H3C2.20435 20 1.44151 19.6837 0.878906 19.1211C0.316297 18.5585 0 17.7957 0 17ZM20 7C20 7.55228 19.5523 8 19 8C18.4477 8 18 7.55228 18 7V3.41406L8.70703 12.707C8.31651 13.0976 7.68349 13.0976 7.29297 12.707C6.90244 12.3165 6.90244 11.6835 7.29297 11.293L16.5859 2H13C12.4477 2 12 1.55228 12 1C12 0.447715 12.4477 0 13 0H19C19.5523 0 20 0.447715 20 1V7Z" fill="currentColor"/>
                    </svg>
                  </span>
                </a>
              </li>
              {galleryEnabled && (
                <NavItem
                  to="/galerie"
                  label="Galerie"
                  onClick={onClose}
                  delay={0.25 + schedules.length * 0.1}
                />
              )}
              <NavItem
                to="/entdecken"
                label="Entdecken"
                onClick={onClose}
                delay={0.27 + schedules.length * 0.1}
              />
              {isLoggedIn && (
                <NavItem
                  to="/profil/datenschutz"
                  label="Datenschutz & Consent"
                  onClick={onClose}
                  delay={0.3 + schedules.length * 0.1}
                />
              )}
              {isLoggedIn && (role === 'host' || role === 'admin') && (
                <NavItem
                  to="/host"
                  label="🎡 Host-Cockpit"
                  onClick={onClose}
                  delay={0.4 + schedules.length * 0.1}
                />
              )}
              {isLoggedIn && role === 'speaker' && (
                <NavItem
                  to="/host"
                  label="🎡 Meine Gondel"
                  onClick={onClose}
                  delay={0.4 + schedules.length * 0.1}
                />
              )}
              {isLoggedIn && role === 'admin' && (
                <NavItem
                  to="/admin"
                  label="⚙️ Admin"
                  onClick={onClose}
                  delay={0.5 + schedules.length * 0.1}
                />
              )}
            </ul>
          </nav>
        </div>

        {/* Auth Button */}
        <div className="px-8 pb-8 pt-4">
          {isLoggedIn ? (
            <div className="space-y-3">
              <button
                onClick={handleAuthAction}
                className="block w-full text-white text-xl bg-eggs-dark hover:bg-eggs-dark-hover px-6 py-3 rounded-full text-center font-medium transition-all duration-200 min-h-[44px]"
              >
                Abmelden
              </button>
              <div className="text-center text-base text-gray-600 mb-2">
                {name}! <br />
                {showRole && (
                  <span className="inline-block px-3 py-1 mb-2 text-sm font-semibold text-eggs-dark bg-eggs-yellow rounded-full">
                    {role.toUpperCase()}
                  </span>
                )}
                {role === 'admin' && (
                  <span className="block mb-2">
                    <span className="inline-flex items-center gap-1">
                      <select
                        value={probeIndustry}
                        onChange={(e) => handleIndustryChange(e.target.value)}
                        className="px-2 py-1 text-xs rounded-full border border-gray-300 bg-white min-h-[32px]"
                        aria-label="Probe Industry Override"
                      >
                        <option value="">🔍 Industry: auto</option>
                        <option value="insurance">insurance</option>
                        <option value="automotive">automotive</option>
                        <option value="banking">banking</option>
                        <option value="telco">telco</option>
                        <option value="media">media</option>
                        <option value="travel">travel</option>
                        <option value="b2b_industry">b2b_industry</option>
                        <option value="pharma">pharma</option>
                        <option value="generic">generic</option>
                      </select>
                      {probeIndustry && (
                        <button
                          onClick={() => handleIndustryChange('')}
                          className="text-xs text-gray-400 hover:text-red-500 min-w-[24px] min-h-[24px]"
                          aria-label="Industry Override zurücksetzen"
                        >
                          ✕
                        </button>
                      )}
                    </span>
                  </span>
                )}
                <span className="block font-semibold text-eggs-yellow">
                  Du bist eggstra special! 🥚
                </span>
              </div>
            </div>
          ) : (
            <NavLink
              to="/login"
              onClick={onClose}
              className="block text-eggs-dark text-xl bg-eggs-yellow hover:bg-eggs-dark hover:text-eggs-yellow px-6 py-3 rounded-full text-center font-medium transition-all duration-200 min-h-[44px]"
            >
              Login
            </NavLink>
          )}
        </div>
      </div>
    </div>
  )
}

export default BurgerMenu
